import React, { useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import LinkIcon from '@atlaskit/icon/core/link';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';

import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { SearchResult } from '../../../../../../common/ui/search-result';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type { searchDefaultRenderer_AtlassianSearchPage$key } from './__generated__/searchDefaultRenderer_AtlassianSearchPage.graphql';

export const SearchDefaultRenderer = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	mainContentWidth,
}: SearchResultProps<searchDefaultRenderer_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchDefaultRenderer_AtlassianSearchPage on SearchResult {
				__typename
				id
				title
				description
				type
				url
			}
		`,
		node,
	);
	const { id, url, type, title, description } = data;

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(id);
		return {
			id,
			containerId: null,
			spaceId: null,
			contentType: type,
			entityType: type,
			contentId,
			product,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [id, type]);

	const onResultClicked = useFireResultClicked({ metadata, position });
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({ metadata, position });
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const enrichedTitle = enrichExcerpt(title);
	const encrichedBody = description && (
		<DescriptionBody description={processExcerpt(description)} />
	);

	return (
		<SearchResult
			{...data}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={url}
			title={enrichedTitle}
			icon={<Fallback1PIcon />}
			headerMetadataParts={[]}
			description={encrichedBody}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
		/>
	);
};

export const Fallback1PIcon = (): React.JSX.Element => (
	<LinkIcon color="currentColor" spacing="spacious" label="" />
);
